import { Req } from '@cbo/shared-library';
import { FindSitesByCriteria } from '@cbo/shared-library/request/site-management.request';
import { ExecuteCustomReportRequest } from './index';

/**
 * USER PREFERENCES
 */
const userPreferencesKeys = {
  preferences: (orgId?: string) => ['userPreferences', orgId] as const,
};

const dashboardConfigurationKeys = {
  dashboardConfiguration: () => ['userDashboardConfiguration'] as const,
};
/**
 * COMPANY LINKS
 */
const companyLinksKeys = {
  companyLinks: (organizationId?: string) => ['companyLinks', organizationId] as const,
  sideBarCompanyLinks: (organizationId?: string) => ['sideBarCompanyLinks', organizationId] as const,
};

/**
 * FISCAL CALENDAR
 */
const fiscalCalendarKeys = {
  fiscalCalendarById: (userId: string, organizationId?: string) => ['fiscalCalendar', userId, organizationId] as const,
  fiscalCalendarPreviewById: (userId: string, year: number) => ['fiscalCalendarPreview', userId, year] as const,
};

/**
 * PAYROLL CALENDAR
 */
const payrollCalendarKeys = {
  calendarBySiteId: (siteId: string | null) => (siteId ? ['payrollCalendar', siteId] : ['payrollCalendar']),
  filtersBySiteId: (siteId: string) => ['payrollCalendar', 'filters', siteId] as const,
};

const notificationSettingsKeys = {
  all: ['allNotificationSettings'] as const,
  allNotificationSettings: (organizationId?: string) => [...notificationSettingsKeys.all, organizationId] as const,
  notificationSettingsById: (notificationSettingsId: string) =>
    ['notificationSettingsById', notificationSettingsId] as const,
  allEventTypes: (organizationId?: string) => ['allEventTypes', organizationId] as const,
};

const notificationsKeys = {
  notifications: (keys: string[]) => [...keys] as const,
  totalUnread: () => ['totalUnreadNotifications'] as const,
};

/**
 * SITE MANAGEMENT
 */
const siteManagementKeys = {
  allSites: (siteIds?: string[], organizationId?: string) => ['allSites', siteIds, organizationId] as const,
  allSitesByCriteria: (criteria: FindSitesByCriteria) => ['allSitesByCriteria', criteria] as const,
  siteDetailsDataById: (siteId: string) => ['siteDetailsDataById', siteId] as const,
  siteDataByIds: (siteIds: string[]) => ['siteDataByIds', siteIds] as const,
};

/**
 * AUDIT LOG
 */
const auditLogKeys = {
  allAuditLogs: (filter: Req.AuditLog.FilterRequest, organizationId?: string) =>
    ['allAuditLogs', filter, organizationId] as const,
  allAuditLogFilters: (organizationId?: string) => ['allAuditLogFilters', organizationId] as const,
};

/**
 * GL Mapping
 */
const glMappingKeys = {
  allGlMappings: (dataTypes?: string[], entityNames?: string[], glAccountNames?: string[]) =>
    ['allGlMappings', dataTypes, entityNames, glAccountNames] as const,
  jobCodeMappings: (dataTypes?: string[], entityNames?: string[], glAccountNames?: string[]) =>
    ['jobCodeMappings', dataTypes, entityNames, glAccountNames] as const,
  glMappingFilters: ['glMappingFilters'] as const,
};

/**
 * POS
 */
const posKeys = {
  pos: (orgId?: string) => ['pos', orgId] as const,
};

/**
 * CUSTOM REPORTS
 */
const customReportsKeys = {
  // Base keys — use these for prefix-based invalidation so all org-scoped variants are matched
  all: ['customReports'] as const,
  sharedAll: ['sharedCustomReports'] as const,
  datasetsAll: ['datasets'] as const,

  // Org-scoped keys — use these when registering queries
  // When organizationId is absent the base key is returned so prefix invalidation still works
  customReportById: (id: number | null) => ['customReport', id] as const,
  customReports: (organizationId?: string) =>
    organizationId ? ([...customReportsKeys.all, organizationId] as const) : customReportsKeys.all,
  sharedCustomReports: (organizationId?: string, userId?: string, userRoles?: string[]) =>
    organizationId
      ? ([...customReportsKeys.sharedAll, organizationId, userId, userRoles] as const)
      : customReportsKeys.sharedAll,
  datasets: (organizationId?: string) =>
    organizationId ? ([...customReportsKeys.datasetsAll, organizationId] as const) : customReportsKeys.datasetsAll,
  allCustomReports: () => ['allCustomReports'] as const,
  executeReport: (request: ExecuteCustomReportRequest) => ['customReports', 'execute', request] as const,
  reportShareDetails: (reportId: number) => ['customReportShares', reportId] as const,
};

/**
 * Custom Metrics
 */
const customMetricKeys = {
  allCustomMetrics: (page: number, limit: number) => ['allCustomMetrics', { page, limit }] as const,
  customMetricById: (id: string) => ['customMetricById', id] as const,
  customMetricReports: (metricId: string) => ['customMetricReports', metricId] as const,
};

/**
 * ORGANIZATION USERS
 */
const organizationUsersKeys = {
  organizationUsers: () => ['organizationUsers'] as const,
};

/**
 * PREDEFINED REPORTS
 */
const predefinedReportKeys = {
  predefinedReport: () => ['predefinedReport'] as const,
};

/**
 * PREDEFINED REPORTS
 */
const ReportListKeys = {
  ReportList: () => ['ReportList'] as const,
  ReportDetails: (id: string) => ['ReportDetails', id] as const,
};

/**
 * SCHEDULED REPORTS
 */
const scheduledReportsKeys = {
  all: ['scheduledReports'] as const,
  list: () => [...scheduledReportsKeys.all, 'list'] as const,
  details: (id: string) => [...scheduledReportsKeys.all, 'details', id] as const,
  byReportId: (reportId: number) => [...scheduledReportsKeys.all, 'byReportId', reportId] as const,
};

const adminKeys = {
  userPreferences: userPreferencesKeys,
  companyLinks: companyLinksKeys,
  fiscalCalendar: fiscalCalendarKeys,
  payrollCalendar: payrollCalendarKeys,
  notificationSettings: notificationSettingsKeys,
  notifications: notificationsKeys,
  siteManagement: siteManagementKeys,
  auditLog: auditLogKeys,
  glMapping: glMappingKeys,
  dashboardConfiguration: dashboardConfigurationKeys,
  pos: posKeys,
  customReports: customReportsKeys,
  customMetrics: customMetricKeys,
  organizationUsers: organizationUsersKeys,
  predefinedReport: predefinedReportKeys,
  ReportList: ReportListKeys,
  scheduledReports: scheduledReportsKeys,
};

export default adminKeys;
